import { inquiryHref } from '@/lib/engagements';
import { useMemo, useState } from 'react';
import { parseInventory, classify, inventoryCsv, CATEGORY_ORDER, CATEGORY_LABEL, type Category } from '@/lib/tools/component-inventory';
import { downloadJson } from '@/lib/tools/download';
import { Link } from 'react-router-dom';
import FAQ, { type FAQItem } from '@/components/FAQ';
import Callout from '@/components/Callout';
import {
  Field,
  Panel,
  ReadingList,
  Stat,
  Table,
  TD,
  TH,
  TextArea,
  ToolIntro,
  ToolSection,
} from '@/components/ToolKit';

// Browser classification aid; no environment connection or installed package.

const EXAMPLE = `ContentTracker
Folders_g
FrameworkFolders
DesktopIntegrationSuite
RecordsManagement
SiteStudio
AppAdapterEBS
AcmeInvoiceRouting /u01/app/oracle/ucm/cs/custom/AcmeInvoiceRouting/AcmeInvoiceRouting.hda enabled
LegalHoldExport /u01/app/oracle/ucm/cs/custom/LegalHoldExport/LegalHoldExport.hda disabled
WccFilePlanSync
InvoiceMetadataValidator`;

const FAQS: FAQItem[] = [
  {
    q: 'What can I paste?',
    a: 'Component names one per line, as shown in Component Manager; a tab-separated name, path and enabled/disabled state; or a Components ResultSet block from the component registry .hda files under the IntradocDir. Blank lines and comment lines starting with # are ignored, and duplicate names are collapsed.',
  },
  {
    q: 'What does “not recognized” mean?',
    a: 'That the name is not in the reference list of Oracle-shipped components and does not match an Oracle naming pattern or a custom path. It is not a verdict. Open the component’s .hda and manifest: the vendor, description and classpath settle whether it is Oracle, third-party or built in-house.',
  },
  {
    q: 'Does anything leave the browser?',
    a: 'No. The listing is parsed in the page and nothing is stored or transmitted. Use the copy or download controls to export it locally.',
  },
  {
    q: 'Can an engineer inspect the environment?',
    a: 'An Estate Review can scope deeper inspection for your releases and access constraints. Any installed collector needs a separate review of its package, compatibility, permissions, data handling and removal procedure. This page does not supply a verified installed edition.',
  },
];

const WccComponentInventory = () => {
  const [text, setText] = useState('');
  const [copied, setCopied] = useState<'idle' | 'done' | 'failed'>('idle');

  const parsed = useMemo(() => parseInventory(text), [text]);
  const rows = useMemo(() => {
    const list = parsed.rows.map(classify);
    return list.sort(
      (a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category) || a.name.localeCompare(b.name),
    );
  }, [parsed]);

  const count = (cat: Category) => rows.filter((r) => r.category === cat).length;
  const disabled = rows.filter((r) => r.state && /^(disabled|false|off)$/i.test(r.state)).length;

  const copyCsv = async () => {
    const csv = inventoryCsv(rows);
    try {
      await navigator.clipboard.writeText(csv);
      setCopied('done');
    } catch {
      setCopied('failed');
    }
    window.setTimeout(() => setCopied('idle'), 2500);
  };

  return (
    <>
      <ToolIntro>
        <p>
          The component layer is where a WebCenter Content estate keeps its customizations, and it is the layer a 14c
          upgrade has to account for one component at a time. Paste the component listing and this sorts it into
          Oracle-shipped, Oracle-family, custom and not-recognized, counts each group, flags anything disabled, and
          attaches the notes that matter for upgrade planning. Nothing leaves the browser.
        </p>
      </ToolIntro>

      <ToolSection id="input" title="The listing">
        <Panel>
          <Field
            label="Component listing"
            htmlFor="listing"
            hint="One name per line, optionally followed by a tab-separated path and enabled/disabled. A Components ResultSet from the registry .hda files also works."
          >
            <TextArea
              id="listing"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={'ContentTracker\nFrameworkFolders\nAcmeInvoiceRouting /u01/.../custom/AcmeInvoiceRouting/AcmeInvoiceRouting.hda enabled'}
              rows={10}
              maxLength={200000}
            />
          </Field>
          <div className="mt-5 flex flex-wrap gap-3">
            <button type="button" className="btn-outline-forest" onClick={() => setText(EXAMPLE)}>
              Load an example
            </button>
            {text && (
              <button type="button" className="btn-outline-forest" onClick={() => setText('')}>
                Clear
              </button>
            )}
          </div>
        </Panel>
      </ToolSection>

      {parsed.error && <p role="alert" className="mt-5 border border-forest p-5 text-body">{parsed.error}</p>}
      {parsed.duplicates > 0 && <p role="status" className="mt-3 text-small">{parsed.duplicates} repeated component names were collapsed. Check the source for distinct instances or conflicting states.</p>}
      {rows.length > 0 && (
        <ToolSection id="result" title="Inventory">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4" role="status">
            <Stat label="Components" value={rows.length} note={disabled ? `${disabled} disabled` : 'none marked disabled'} />
            <Stat label="Oracle-shipped" value={count('oracle')} note="In the reference list" />
            <Stat label="Oracle-family" value={count('family')} note="By naming pattern; verify" />
            <Stat label="Custom or unrecognized" value={count('custom') + count('unknown')} note={`${count('custom')} custom · ${count('unknown')} not recognized`} />
          </div>
          <div className="mt-8">
            <Table caption="Sorted component inventory">
              <thead>
                <tr>
                  <TH>Component</TH>
                  <TH>Category</TH>
                  <TH>State</TH>
                  <TH>Notes</TH>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.name}>
                    <TD>
                      <span className="font-bold">{r.name}</span>
                      {r.location && (
                        <span className="mt-1 block break-all font-mono text-[0.875rem] leading-[1.4] text-forest/80">
                          {r.location}
                        </span>
                      )}
                    </TD>
                    <TD nowrap>
                      {r.category === 'custom' || r.category === 'unknown' ? (
                        <span className="border-l-[3px] border-l-brass pl-3">{CATEGORY_LABEL[r.category]}</span>
                      ) : (
                        CATEGORY_LABEL[r.category]
                      )}
                    </TD>
                    <TD nowrap>{r.state ?? '—'}</TD>
                    <TD>{r.note ?? '—'}</TD>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <button type="button" className="btn-forest" onClick={copyCsv}>
              Copy inventory as CSV
            </button>
            <button type="button" className="btn-outline-forest" onClick={() => downloadJson('ecmworks-component-inventory.json', { modelVersion: '2026-09-16.2', basis: 'Name/path heuristics; verify against exact-release manifests', duplicateNames: parsed.duplicates, rows })}>Download inventory</button>
            {copied === 'done'  && <span role="status" className="text-small">Copied. Spreadsheet formulas are escaped as text.</span>}
            {copied === 'failed' && <span role="alert" className="text-small">Copy failed; select the table and copy it instead.</span>}
          </div>
        </ToolSection>
      )}

      <ToolSection id="reading" title="How to read the result">
        <ReadingList
          items={[
            {
              title: 'Custom and not-recognized components are the upgrade work',
              body: 'Each one needs an owner, a purpose and a decision: carry it forward, rebuild it against the 14c services, or retire it. A reference-list match does not establish target-release availability or compatibility.',
            },
            {
              title: '“Oracle-family” is a naming pattern, not proof',
              body: 'Names that start with Oracle, WebCenter, AppAdapter and the like are usually Oracle’s, but a custom component can be named the same way. Confirm against the component’s manifest.',
            },
            {
              title: 'Disabled is a decision, not an answer',
              body: 'A disabled component may be dormant, or may be switched off because it broke on the last patch. Find out which before it is either dropped or carried.',
            },
            {
              title: 'The listing is the catalog, not the dependency map',
              body: 'Which components depend on which is not visible from names. Those questions need separate evidence from manifests, configuration, source and runtime behavior.',
            },
          ]}
        />
      </ToolSection>

      <ToolSection id="installed" title="From a list to an evidence-based inventory">
        <div className="max-w-[46rem]">
          <p className="text-body">The browser tool classifies names and paths. A deeper review needs the exact releases, component manifests, configuration, dependencies, source availability and observed business use. Classification is a starting point, not a compatibility verdict.</p>
          <Callout kind="decision"><p>Use an <Link to="/estate-review">Estate Review</Link> to scope that work, or <Link to={inquiryHref('/tools/wcc-component-inventory')}>discuss the inventory</Link>. Agree any installed collection method, package, permissions, network behavior and removal procedure before use. No verified installation package is offered on this page.</p></Callout>
          <p className="text-body">Continue with the <Link to="/tools/webcenter-content-inspector" className="link">discovery readiness check</Link> and the <Link to="/upgrades/webcenter-content-upgrade-validation-checklist" className="link">upgrade validation checklist</Link>.</p>
        </div>
      </ToolSection>

      <div className="mt-16 max-w-[46rem]">
        <FAQ items={FAQS} />
      </div>
    </>
  );
};

export default WccComponentInventory;
